import type { CityConfig } from "../city-template/types";

export const stocktonConfig: CityConfig = {
  city: "Stockton",
  state: "California",
  stateAbbr: "CA",
  slug: "stockton",
  route: "/cities/stockton",
  title: "Emergency Passport Stockton, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Stockton, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for San Joaquin County and Central Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Stockton, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Stockton and San Joaquin County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stockton%20California%20waterfront%20cityscape%20at%20golden%20hour%20with%20modern%20downtown%20buildings%20along%20the%20San%20Joaquin%20River%2C%20waterfront%20promenade%20with%20warm%20sunset%20light%20reflecting%20on%20the%20water%2C%20clean%20modern%20urban%20skyline%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20modern%20Central%20Valley%20city%20environment%20with%20warm%20amber%20tones&width=1800&height=700&seq=stockton-hero-001&orientation=landscape",
  heroImageAlt: "Stockton California skyline at golden hour with waterfront along the San Joaquin River",
  geoPosition: "37.9577;-121.2908",
  geoPlacename: "Stockton, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in downtown SF — approximately 80–90 minutes from Stockton via I-580",
  },
  nearbyCitiesZips: [
    { name: "Stockton", zips: "95201–95207, 95209, 95210, 95212, 95215, 95219" },
    { name: "Lodi", zips: "95240, 95242" },
    { name: "Tracy", zips: "95304, 95376, 95377" },
    { name: "Manteca", zips: "95336, 95337" },
    { name: "Lathrop", zips: "95330" },
    { name: "Ripon", zips: "95366" },
    { name: "Escalon", zips: "95320" },
    { name: "Galt", zips: "95632" },
    { name: "Modesto", zips: "95350–95358" },
    { name: "Turlock", zips: "95380, 95382" },
    { name: "Ceres", zips: "95307" },
    { name: "Elk Grove", zips: "95624, 95757, 95758" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent San Joaquin County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "Fresno", route: "/cities/fresno" },
    { name: "Bakersfield", route: "/cities/bakersfield" },
    { name: "Irvine", route: "/cities/irvine" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Stockton remotely via secure courier. We do not have a walk-in office in Stockton — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for San Joaquin County travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of San Joaquin County",
  serviceAreaIntro: "From the downtown waterfront to Lincoln Village, from Brookside to Spanos Park — Fast Passport Center serves the entire Stockton community. We regularly help travelers from Lodi, Tracy, Manteca, and Modesto get their passports fast.",
  nearbyCitiesIntro: "We serve all Stockton neighborhoods and nearby Central Valley cities including Lodi, Tracy, Manteca, Ripon, Galt, and Modesto. If you are in San Joaquin County or nearby Stanislaus County, we have you covered.",
  howItWorksIntro: "Most Stockton travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Stockton Metropolitan Airport (SCK) serving as a growing regional hub, Central Valley travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Stockton and San Joaquin County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Stockton? Here are the most common ones we hear from San Joaquin County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent San Joaquin County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Robert H.",
      location: "Stockton, CA",
      rating: 5,
      text: "My flight to the Philippines was in 2 days and my passport had expired. Fast Passport Center got me a new one in under 48 hours. Absolutely saved my family visit.",
      service: "Emergency Passport",
    },
    {
      name: "Amanda L.",
      location: "Lodi, CA",
      rating: 5,
      text: "Needed to renew for a cruise to Mexico. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Great service for Central Valley residents.",
      service: "Passport Renewal",
    },
    {
      name: "Michael T.",
      location: "Tracy, CA",
      rating: 5,
      text: "Lost my passport the week before my business trip to London. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Jessica R.",
      location: "Manteca, CA",
      rating: 5,
      text: "As a frequent international traveler from the Central Valley, I can't afford passport delays. Fast Passport Center has been reliable every time. Highly recommend.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "stockton-1",
      name: "Stockton Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "401 E Fremont St",
      city: "Stockton",
      state: "CA",
      zip: "95202",
      phone: "(209) 463-4113",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Stockton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "stockton-2",
      name: "Lodi Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 W Elm St",
      city: "Lodi",
      state: "CA",
      zip: "95240",
      phone: "(209) 368-9113",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Lodi",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Lodi residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "stockton-3",
      name: "Tracy Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "375 W Ninth St",
      city: "Tracy",
      state: "CA",
      zip: "95376",
      phone: "(209) 835-4982",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Tracy",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "stockton-4",
      name: "Manteca Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "211 E Yosemite Ave",
      city: "Manteca",
      state: "CA",
      zip: "95336",
      phone: "(209) 823-6241",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Manteca",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern San Joaquin County residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "stockton-5",
      name: "Modesto Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1212 Ninth St",
      city: "Modesto",
      state: "CA",
      zip: "95354",
      phone: "(209) 523-5371",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from downtown Modesto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Stanislaus County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "stockton-6",
      name: "San Joaquin County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "44 N San Joaquin St",
      city: "Stockton",
      state: "CA",
      zip: "95202",
      phone: "(209) 468-3939",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from the Stockton courthouse",
      website: "https://www.sjc.gov/",
      websiteLabel: "San Joaquin County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Central Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at San Joaquin County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Stockton applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Central Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "San Joaquin County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Stockton." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Stockton address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Stockton before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Stockton for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Central Valley travelers." },
    { q: "How fast can I renew my passport in Stockton?", a: "Fast Passport Center offers fast passport renewal in Stockton in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Stockton in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Stockton?", a: "Same-day passport appointments require a drive to the SF Regional Passport Agency. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Stockton residents." },
    { q: "Can I expedite a child passport in Stockton?", a: "Yes. Fast Passport Center expedites child passports in Stockton for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Stockton with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Stockton?",
      a: "With Fast Passport Center's emergency courier service, Stockton travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Stockton?",
      a: "Popular options include the Stockton Main Post Office (95202), Lodi Post Office (95240), Tracy Post Office (95376), and San Joaquin County Clerk (95202). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Stockton?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Lodi, Tracy, or Manteca?",
      a: "We serve all of San Joaquin County including Lodi, Tracy, Manteca, Ripon, Lathrop, and Escalon. Our overnight shipping labels work from any UPS location, so your location within the county doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Stockton?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Stockton?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Stockton and Lodi offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Stockton?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Stockton?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk San Joaquin County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Stockton", address: "5755 Pacific Ave, Stockton, CA 95207", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Stockton", address: "4555 N Pershing Ave, Stockton, CA 95207", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Stockton", address: "2233 Grand Canal Blvd, Stockton, CA 95207", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Lodi", address: "701 S Ham Ln, Lodi, CA 95242", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Tracy", address: "3010 W Grant Line Rd, Tracy, CA 95304", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Manteca", address: "1150 S Main St, Manteca, CA 95337", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Tracy", address: "2620 S Tracy Blvd, Tracy, CA 95376", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Stockton", address: "1616 E Hammer Ln, Stockton, CA 95210", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Stockton",
      address: "3520 E Hammer Ln, Stockton, CA 95212",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Lodi",
      address: "1331 S Ham Ln, Lodi, CA 95242",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Tracy",
      address: "30 W 10th St, Tracy, CA 95376",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Modesto",
      address: "124 L St, Modesto, CA 95351",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Stanislaus County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SCK",
      name: "Stockton Metropolitan Airport",
      distance: "5 miles from downtown Stockton",
      address: "5000 S Airport Way, Stockton, CA 95206",
      note: "Regional airport with flights to Las Vegas, Phoenix, and Mexico. Growing international connections.",
      flightUrl: "https://www.stocktonairport.com/",
      flightLabel: "Stockton Airport",
      featured: true,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "50 miles from Stockton",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Major international airport for Northern California. Full international flight options.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "85 miles from Stockton",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and worldwide destinations.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the California Department of Public Health Vital Records. Online, mail, or in-person options available for San Joaquin County residents.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records. Required for estate and legal matters in Stockton and the Central Valley.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the San Joaquin County Clerk-Recorder. Certified copies available for name changes and legal purposes.", url: "https://www.sjgov.org/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the San Joaquin County Superior Court. Required for name change passport corrections and legal documentation.", url: "https://www.sjcourts.org/" },
  ],
};